/**
 * Mi Boleta — Checkout: lógica de pago a cuotas (pago diferido).
 *
 * Matemática pura y datos del dominio. Sin UI y sin red a propósito: se testea
 * con vitest sin montar React.
 *
 * Unidad monetaria: **centavos**, igual que `Money.amount_cents` en
 * `src/lib/payment/types.ts`. Nunca floats de pesos: redondear plata en punto
 * flotante es cómo se pierde $1 en el desglose.
 */

// ── Métodos de pago (wireflow 1.7 · Pago) ───────────────────────────
export type PaymentMethodId = 'credit_card' | 'pse' | 'nequi' | 'daviplata' | 'digital_wallet';

export interface PaymentMethod {
  id: PaymentMethodId;
  label: string;
  hint: string;
  /** Solo el crédito difiere: PSE/Nequi/Daviplata son débito inmediato. */
  supportsInstallments: boolean;
}

export const PAYMENT_METHODS: readonly PaymentMethod[] = [
  {
    id: 'credit_card',
    label: 'Tarjeta de crédito',
    hint: 'Visa · Mastercard · Amex',
    supportsInstallments: true,
  },
  { id: 'pse', label: 'PSE', hint: 'Débito desde tu banco', supportsInstallments: false },
  { id: 'nequi', label: 'Nequi', hint: 'Pago desde tu app', supportsInstallments: false },
  { id: 'daviplata', label: 'Daviplata', hint: 'Pago desde tu app', supportsInstallments: false },
  {
    id: 'digital_wallet',
    label: 'Billeteras digitales',
    hint: 'Apple Pay · Google Pay',
    supportsInstallments: false,
  },
] as const;

export function supportsInstallments(methodId: PaymentMethodId | null): boolean {
  return PAYMENT_METHODS.some((m) => m.id === methodId && m.supportsInstallments);
}

// ── Opciones de diferido ────────────────────────────────────────────
/** Plazos habituales de las franquicias en Colombia. */
export const INSTALLMENT_OPTIONS: readonly number[] = [1, 3, 6, 12, 24, 36] as const;

/**
 * Texto legal obligatorio bajo el desglose.
 *
 * No es copy decorativo: el valor de la cuota que mostramos es el capital
 * dividido, SIN intereses — la financiación la define el banco emisor, no MB.
 * Si alguien cambia este texto, que sea una decisión, no un descuido.
 */
export const INSTALLMENT_LEGAL_NOTE = 'La tasa de interés final depende de la entidad bancaria';

// ── Plan de cuotas ──────────────────────────────────────────────────
export interface InstallmentPlan {
  /** Número de cuotas elegido. */
  installments: number;
  /** Valor total de la orden, en centavos. */
  totalCents: number;
  /** Cuota mensual aproximada (la que se muestra), en centavos. */
  perInstallmentCents: number;
  /**
   * Última cuota, en centavos. Absorbe el residuo de la división para que
   * `per * (n-1) + last === total` exacto: si no, el desglose no cuadra
   * contra el total y el fan lo nota.
   */
  lastInstallmentCents: number;
  /** `true` si hay diferido real (>1 cuota) → se muestra el desglose. */
  isDeferred: boolean;
}

/**
 * Divide el total en `installments` cuotas iguales, sin intereses.
 *
 * Los intereses NO se calculan acá a propósito: dependen del banco emisor
 * (ver `INSTALLMENT_LEGAL_NOTE`). Esto es capital dividido, y así se rotula.
 */
export function calculateInstallmentPlan(
  totalCents: number,
  installments: number,
): InstallmentPlan {
  if (!Number.isInteger(totalCents) || totalCents < 0) {
    throw new Error(`totalCents debe ser un entero >= 0 (recibido: ${totalCents})`);
  }
  if (!Number.isInteger(installments) || installments < 1) {
    throw new Error(`installments debe ser un entero >= 1 (recibido: ${installments})`);
  }

  const perInstallmentCents = Math.floor(totalCents / installments);
  const remainder = totalCents - perInstallmentCents * installments;

  return {
    installments,
    totalCents,
    perInstallmentCents,
    lastInstallmentCents: perInstallmentCents + remainder,
    isDeferred: installments > 1,
  };
}

// ── Formato COP ─────────────────────────────────────────────────────
/**
 * Formatea centavos como pesos colombianos: `21000000` → `$210.000`.
 *
 * Separador de miles manual en vez de `Intl`/`toLocaleString`: el resultado
 * tiene que ser idéntico acá y en la app móvil (Hermes), donde el soporte de
 * locales depende del build. El COP no usa decimales.
 */
export function formatCop(cents: number): string {
  const pesos = Math.round(cents / 100);
  const grouped = String(Math.abs(pesos)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${pesos < 0 ? '-' : ''}$${grouped}`;
}

/** `3` → `'3 cuotas'`, `1` → `'1 cuota'`. */
export function formatInstallmentLabel(count: number): string {
  return count === 1 ? '1 cuota' : `${count} cuotas`;
}
